/**
 * SubscribersDashboard — Admin view for newsletter subscribers.
 * Lists subscribers from /api/newsletter/subscribers (admin-only), with a
 * confirmed-only toggle, search, summary counts, and CSV export.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { config } from '../../config';
import './SubscribersDashboard.css';

// ── Types ───────────────────────────────────────────────────────────────────

interface Subscriber {
  email: string;
  confirmed: boolean;
  consent_timestamp: string;
  consent_ip: string | null;
  confirmed_at: string | null;
  source: string;
  created_at: string;
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-CA', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'America/Vancouver',
  });
}

function toCsv(rows: Subscriber[]): string {
  const header = ['email', 'confirmed', 'source', 'consent_timestamp', 'consent_ip', 'confirmed_at', 'created_at'];
  const esc = (v: string | boolean | null) => {
    const s = v === null || v === undefined ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = rows.map((r) => [
    r.email, r.confirmed, r.source, r.consent_timestamp, r.consent_ip, r.confirmed_at, r.created_at,
  ].map(esc).join(','));
  return [header.join(','), ...lines].join('\n');
}

// ── Main Component ───────────────────────────────────────────────────────────

const SubscribersDashboard: React.FC = () => {
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [confirmedOnly, setConfirmedOnly] = useState(false);

  const fetchSubscribers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { getValidToken } = await import('../../utils/tokenRefresh');
      const token = await getValidToken();
      const res = await axios.get(`${config.BACKEND_URL}/api/newsletter/subscribers`, {
        params: { confirmed_only: confirmedOnly },
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setSubscribers(res.data || []);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to load subscribers.');
    } finally {
      setLoading(false);
    }
  }, [confirmedOnly]);

  useEffect(() => { fetchSubscribers(); }, [fetchSubscribers]);

  const confirmedCount = useMemo(
    () => subscribers.filter((s) => s.confirmed).length,
    [subscribers],
  );

  const filtered = useMemo(() => {
    if (!search.trim()) return subscribers;
    const q = search.toLowerCase();
    return subscribers.filter((s) => s.email.toLowerCase().includes(q) || (s.source || '').toLowerCase().includes(q));
  }, [subscribers, search]);

  const handleExport = useCallback(() => {
    const csv = toCsv(filtered);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `oara-newsletter-subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [filtered]);

  return (
    <div className="sd-container">
      {/* Header */}
      <div className="sd-header">
        <div>
          <h2 className="sd-title">📬 Newsletter Subscribers</h2>
          <p className="sd-subtitle">
            {loading
              ? 'Loading…'
              : `${subscribers.length} ${confirmedOnly ? 'confirmed' : 'total'}${confirmedOnly ? '' : ` · ${confirmedCount} confirmed`} · CASL double opt-in`}
          </p>
        </div>
        <div className="sd-header-actions">
          <label className="sd-toggle">
            <input
              type="checkbox"
              checked={confirmedOnly}
              onChange={(e) => setConfirmedOnly(e.target.checked)}
            />
            <span>Confirmed only</span>
          </label>
          <input
            className="sd-search"
            type="text"
            placeholder="Search email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button className="sd-export-btn" onClick={handleExport} disabled={loading || filtered.length === 0}>
            ⬇ Export CSV
          </button>
          <button className="sd-refresh-btn" onClick={fetchSubscribers} disabled={loading}>
            {loading ? '…' : '↺ Refresh'}
          </button>
        </div>
      </div>

      {/* States */}
      {loading && <div className="sd-state">Loading subscribers…</div>}
      {error   && <div className="sd-state sd-state-error">{error}</div>}

      {!loading && !error && subscribers.length === 0 && (
        <div className="sd-state sd-state-empty">
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📭</div>
          <p>No {confirmedOnly ? 'confirmed ' : ''}subscribers yet.</p>
          <p style={{ fontSize: '0.9rem', color: '#64748b' }}>
            Sign-ups from the newsletter form on{' '}
            <a href="https://oarayouthinnovationlab.ca/" target="_blank" rel="noreferrer">oarayouthinnovationlab.ca</a>{' '}
            will appear here once confirmed.
          </p>
        </div>
      )}

      {!loading && !error && subscribers.length > 0 && (
        <div className="sd-table-wrap">
          {search && (
            <div className="sd-filter-note">
              Showing {filtered.length} of {subscribers.length} · <button className="sd-clear-search" onClick={() => setSearch('')}>Clear search</button>
            </div>
          )}
          <table className="sd-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Email</th>
                <th>Status</th>
                <th>Source</th>
                <th>Subscribed</th>
                <th>Confirmed</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s, i) => (
                <tr key={s.email} className="sd-row">
                  <td className="sd-num">{filtered.length - i}</td>
                  <td className="sd-email">{s.email}</td>
                  <td>
                    {s.confirmed
                      ? <span className="sd-badge sd-badge-confirmed">✓ Confirmed</span>
                      : <span className="sd-badge sd-badge-pending">⏳ Pending</span>}
                  </td>
                  <td className="sd-cell-compact">{s.source || '—'}</td>
                  <td className="sd-date">{formatDate(s.created_at)}</td>
                  <td className="sd-date">{formatDate(s.confirmed_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SubscribersDashboard;
